"use client";

import { useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { MessageSquare, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { UnifiedChatProvider } from "@/context/UnifiedChatContext";
import ChatSurface from "@/components/chat/home/ChatSurface";
import type { Page, Book } from "@/lib/book-types";

export interface BookChatPanelProps {
  book: Book | null;
  page: Page | null;
  open: boolean;
  onClose: () => void;
  initialSessionId?: string | null;
  onSessionResolved?: (sessionId: string) => void;
}

export default function BookChatPanel({
  book,
  page,
  open,
  onClose,
  initialSessionId = null,
  onSessionResolved,
}: BookChatPanelProps) {
  const { t } = useTranslation();
  const [width, setWidth] = useState(360);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    const raw = window.localStorage.getItem("aitoktok.bookChat.width");
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed >= 300 && parsed <= 720) {
      // Hydrate persisted panel width after the SSR-safe default render.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setWidth(parsed);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("aitoktok.bookChat.width", String(width));
  }, [width]);

  function beginResize(event: ReactMouseEvent<HTMLDivElement>) {
    event.preventDefault();
    dragRef.current = { startX: event.clientX, startWidth: width };
    const onMove = (moveEvent: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const next = Math.max(
        300,
        Math.min(720, drag.startWidth + drag.startX - moveEvent.clientX),
      );
      setWidth(next);
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  if (!open) return null;

  return (
    <aside
      className="relative flex h-full shrink-0 flex-col border-l border-[var(--border)] bg-[var(--card)]/40 backdrop-blur"
      style={{ width }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        title={t("Drag to resize")}
        onMouseDown={beginResize}
        className="absolute inset-y-0 left-0 z-10 w-1 cursor-col-resize bg-transparent transition-colors hover:bg-[var(--primary)]/30"
      />
      <header className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-[var(--foreground)]">
            <MessageSquare className="h-4 w-4 text-[var(--primary)]" />
            {t("Page Chat")}
          </div>
          {page?.title && (
            <div className="mt-1 truncate text-[13px] text-[var(--muted-foreground)]">
              {t("Context")}: {page.title}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          className="rounded p-1 text-[var(--muted-foreground)] hover:bg-[var(--background)] hover:text-[var(--foreground)]"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      {/* Isolated chat context per page: the nested provider shadows the
          workspace one so this panel's session/selection never collide with
          Home's, and the key remounts a fresh chat when the page changes. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <UnifiedChatProvider
          key={`${book?.id ?? "none"}:${page?.id ?? "none"}`}
        >
          <ChatSurface
            variant="panel"
            sessionKey={book && page ? `${book.id}:${page.id}` : null}
            initialSessionId={initialSessionId}
            initialKnowledgeBases={book?.knowledge_bases}
            bookReferences={() =>
              book && page ? [{ book_id: book.id, page_ids: [page.id] }] : []
            }
            inputPlaceholder={t("Ask about this page…")}
            host={{ onSessionResolved }}
          />
        </UnifiedChatProvider>
      </div>
    </aside>
  );
}
